import { DeleteOutlined, PlusOutlined, ReloadOutlined } from "@ant-design/icons";
import { App as AntApp, Alert, Button, Drawer, Input, Popconfirm, Select, Space, Tag, Tooltip } from "antd";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { ApiError } from "../api/client.js";
import { useDeleteProperty, usePropertyEntries, useSaveProperty } from "../api/queries.js";
import { useAuth } from "../auth/AuthContext.js";
import { EDITOR_LANGUAGES, detectLanguage, formatTimestamp } from "../format.js";
import { ValueEditor } from "./ValueEditor.js";

export interface OpenProperty {
  name: string;
  initialTarget: string | null;
}

interface PropertyDrawerProps {
  application: string;
  property: OpenProperty | null;
  onClose: () => void;
}

export function PropertyDrawer({ application, property, onClose }: PropertyDrawerProps) {
  const { message } = AntApp.useApp();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const entriesQuery = usePropertyEntries(application, property?.name ?? null);
  const entries = entriesQuery.data ?? [];

  const [selectedTarget, setSelectedTarget] = useState<string | null>(null);
  const [addingTarget, setAddingTarget] = useState(false);
  const [newTarget, setNewTarget] = useState("");
  const [draft, setDraft] = useState("");
  const [language, setLanguage] = useState("text");
  const [description, setDescription] = useState("");
  const [note, setNote] = useState("");
  const [conflict, setConflict] = useState<{ kind: "changed" | "exists"; actualUpdateId: number } | null>(
    null,
  );
  const dirtyRef = useRef(false);

  const entry = entries.find((candidate) => candidate.target === selectedTarget);

  useEffect(() => {
    setSelectedTarget(property?.initialTarget ?? null);
    setAddingTarget(false);
    setConflict(null);
    setNote("");
    setNewTarget("");
    setDraft("");
    setLanguage("text");
    setDescription("");
    dirtyRef.current = false;
  }, [property, application]);

  useEffect(() => {
    if (!property || addingTarget || entries.length === 0) {
      return;
    }
    if (selectedTarget === null) {
      setSelectedTarget(entries[0]?.target ?? null);
      dirtyRef.current = false;
      return;
    }
    // Only correct a selection that no longer exists once the refetch settled —
    // otherwise saving a brand-new target snaps the selection back to the
    // first entry while the entries query is still in flight.
    if (!entries.some((e) => e.target === selectedTarget) && !entriesQuery.isFetching) {
      setSelectedTarget(entries[0]?.target ?? null);
      dirtyRef.current = false;
    }
  }, [entries, property, selectedTarget, addingTarget, entriesQuery.isFetching]);

  useEffect(() => {
    if (addingTarget) {
      if (!dirtyRef.current) {
        setDraft("");
        setLanguage("text");
        setDescription("");
      }
      return;
    }
    if (entry && !dirtyRef.current) {
      setDraft(entry.value);
      setLanguage(detectLanguage(entry.value));
      setDescription(entry.description ?? "");
    }
  }, [entry, addingTarget]);

  const saveMutation = useSaveProperty();
  const deleteMutation = useDeleteProperty();

  if (!property) {
    return null;
  }

  const targetForSave = addingTarget ? newTarget.trim() : selectedTarget;
  const controlUpdateId = addingTarget ? -1 : (entry?.updateId ?? -1);

  const save = () => {
    if (!targetForSave) {
      void message.warning("pick a target first");
      return;
    }
    saveMutation.mutate(
      {
        application,
        name: property.name,
        target: targetForSave,
        value: draft,
        controlUpdateId,
        message: note || undefined,
        description: description.trim() || undefined,
      },
      {
        onSuccess: (result) => {
          void message.success(`saved — update #${result.updateId}`);
          setConflict(null);
          setNote("");
          dirtyRef.current = false;
          if (addingTarget) {
            setAddingTarget(false);
            setSelectedTarget(targetForSave);
            setNewTarget("");
          }
        },
        onError: (error) => {
          if (error instanceof ApiError && error.status === 409) {
            setConflict({
              kind: error.code === "already-exists" ? "exists" : "changed",
              actualUpdateId: error.actualUpdateId ?? -1,
            });
          } else {
            void message.error(error.message);
          }
        },
      },
    );
  };

  const remove = () => {
    if (!entry) {
      return;
    }
    deleteMutation.mutate(
      {
        application,
        name: property.name,
        target: entry.target,
        controlUpdateId: entry.updateId,
        message: note || undefined,
      },
      {
        onSuccess: () => {
          void message.success(`deleted ${property.name} @ ${entry.target}`);
          dirtyRef.current = false;
          if (entries.length <= 1) {
            onClose();
          } else {
            setSelectedTarget(entries.find((e) => e.target !== entry.target)?.target ?? null);
          }
        },
        onError: (error) => void message.error(error.message),
      },
    );
  };

  const reloadLatest = () => {
    if (conflict?.kind === "exists" && addingTarget) {
      // The typed target already holds a value — switch to editing it.
      setAddingTarget(false);
      setSelectedTarget(newTarget.trim());
      setNewTarget("");
    }
    dirtyRef.current = false;
    setConflict(null);
    void queryClient.invalidateQueries({ queryKey: ["entries", application, property.name] });
  };

  const jsonValidity = (() => {
    if (language !== "json" || draft.trim() === "") {
      return null;
    }
    try {
      JSON.parse(draft);
      return true;
    } catch {
      return false;
    }
  })();

  return (
    <Drawer
      open
      onClose={onClose}
      width={760}
      title={
        <Space>
          <span className="mono">{property.name}</span>
          <Tag bordered={false} color="default">
            {application}
          </Tag>
        </Space>
      }
    >
      <div className="drawer-field">
        <span className="microlabel">target</span>
        <Space.Compact style={{ width: "100%" }}>
          {addingTarget ? (
            <Input
              autoFocus
              className="mono"
              placeholder="adigium://prod/api/billing"
              value={newTarget}
              onChange={(event) => setNewTarget(event.target.value)}
            />
          ) : (
            <Select
              style={{ flex: 1 }}
              value={selectedTarget}
              onChange={(target) => {
                setSelectedTarget(target);
                setConflict(null);
                dirtyRef.current = false;
              }}
              options={entries.map((candidate) => ({
                value: candidate.target,
                label: (
                  <span className="mono">
                    {candidate.target} <span className="dim">#{candidate.updateId}</span>
                  </span>
                ),
              }))}
            />
          )}
          <Button
            icon={<PlusOutlined />}
            onClick={() => {
              setAddingTarget(!addingTarget);
              setConflict(null);
              dirtyRef.current = false;
            }}
            type={addingTarget ? "primary" : "default"}
          >
            {addingTarget ? "existing" : "new target"}
          </Button>
        </Space.Compact>
      </div>

      {entry && !addingTarget && (
        <div className="drawer-field mono dim" style={{ fontSize: 11 }}>
          update <b>#{entry.updateId}</b>
          {entry.author ? ` · by ${entry.author}` : ""} · {formatTimestamp(entry.modifiedAt)}
        </div>
      )}

      {conflict !== null && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 14 }}
          message={
            conflict.kind === "exists"
              ? `This target already has a value for this property (update #${conflict.actualUpdateId}).`
              : conflict.actualUpdateId === -1
                ? "This entry no longer exists — it was deleted by someone else."
                : `Changed by someone else — the entry is now at update #${conflict.actualUpdateId}.`
          }
          action={
            <Button size="small" icon={<ReloadOutlined />} onClick={reloadLatest}>
              {conflict.kind === "exists" ? "open existing" : "load latest"}
            </Button>
          }
        />
      )}

      <div className="drawer-field">
        <span className="microlabel">
          value
          {jsonValidity !== null && (
            <Tag
              bordered={false}
              color={jsonValidity ? "success" : "error"}
              style={{ marginLeft: 8, fontSize: 10 }}
            >
              {jsonValidity ? "valid json" : "invalid json"}
            </Tag>
          )}
          {draft.includes("${VAULT:") && (
            <Tooltip title="contains a secret reference — read clients receive the resolved value, the stored reference stays here">
              <Tag bordered={false} color="processing" style={{ marginLeft: 8, fontSize: 10 }}>
                vault reference
              </Tag>
            </Tooltip>
          )}
        </span>
        <ValueEditor
          value={draft}
          onChange={(value) => {
            dirtyRef.current = true;
            setDraft(value);
          }}
          language={language}
          height={340}
        />
        <div style={{ marginTop: 6 }}>
          <Select
            size="small"
            value={language}
            onChange={setLanguage}
            options={EDITOR_LANGUAGES.map((lang) => ({ value: lang, label: lang }))}
            style={{ width: 130 }}
          />
        </div>
      </div>

      <div className="drawer-field">
        <span className="microlabel">description (optional)</span>
        <Input.TextArea
          placeholder="what this property controls"
          value={description}
          onChange={(event) => {
            dirtyRef.current = true;
            setDescription(event.target.value);
          }}
          maxLength={1000}
          autoSize={{ minRows: 1, maxRows: 4 }}
        />
      </div>

      <div className="drawer-field">
        <span className="microlabel">change note (audit)</span>
        <Input
          placeholder="why this change?"
          value={note}
          onChange={(event) => setNote(event.target.value)}
          maxLength={1000}
        />
      </div>

      <Space>
        <Button
          type="primary"
          onClick={save}
          loading={saveMutation.isPending}
          disabled={!addingTarget && !entry}
        >
          {addingTarget ? "Create entry" : "Save"}
        </Button>
        {entry && !addingTarget && (
          <Popconfirm
            title={`Delete ${property.name} @ ${entry.target}?`}
            description="Clients fall back to the value inherited from a less specific target."
            okText="delete"
            okButtonProps={{ danger: true }}
            onConfirm={remove}
          >
            <Button danger icon={<DeleteOutlined />} loading={deleteMutation.isPending}>
              Delete
            </Button>
          </Popconfirm>
        )}
        <span className="dim" style={{ fontSize: 11 }}>
          author: <span className="mono">{user?.username}</span>
        </span>
      </Space>
    </Drawer>
  );
}
